import { useState, useEffect, useRef } from "react";
import logoNoxzi from "../images/DA_NoxZi/Logo-bg-transparent-white.webp";
import {
  Bus,
  Bike,
  Car,
  Footprints,
  MapPin,
  Leaf,
  DoorOpen,
  BadgeEuro,
  Phone,
  Mail,
} from "lucide-react";

const spaces = [
  {
    label: "Terrasse",
    title: "14 m² de terrasse privative",
    text: "Un espace extérieur rare à Chambéry. Idéal pour les repas en plein air, un coin détente végétalisé ou recevoir en belle saison.",
    image: "/images/terrasse.jpeg",
    tag: "Extérieur",
  },
  {
    label: "Salon",
    title: "Un séjour lumineux et généreux",
    text: "Pièce de vie ouverte sur l'extérieur, baignée de lumière naturelle. Un espace modulable pensé pour le confort du quotidien.",
    image: "/images/salon.jpeg",
    tag: "Vie",
  },
  {
    label: "Cuisine",
    title: "Cuisine fonctionnelle et pratique",
    text: "Bien agencée, elle permet une utilisation fluide au quotidien, avec tout le nécessaire à portée de main.",
    image: "/images/cuisine.jpeg",
    tag: "Quotidien",
  },
  {
    label: "Chambres",
    title: "Deux chambres bien séparées",
    text: "Deux espaces nuit distincts et silencieux, adaptés aussi bien à une famille qu'à un investissement locatif.",
    image: "/images/chambre.jpeg",
    tag: "Repos",
  },
];

const galleryImages = [
  { src: "/images/salon.jpeg", label: "Séjour" },
  { src: "/images/cuisine.jpeg", label: "Cuisine" },
  { src: "/images/terrasse.jpeg", label: "Terrasse" },
  { src: "/images/chambre.jpeg", label: "Chambre 1" },
  { src: "/images/chambre2.jpeg", label: "Chambre 2" },
  { src: "/images/salle-bain.jpeg", label: "Salle de bain" },
];

const transportTimes = [
  { icon: Bus, value: "12 min", label: "Bus ligne C" },
  { icon: Bike, value: "7 min", label: "Vélo" },
  { icon: Car, value: "3 min", label: "Voiture" },
  { icon: Footprints, value: "17 min", label: "À pied" },
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.75s ease ${delay}s, transform 0.75s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function LandingPage() {
  const [selected, setSelected] = useState(spaces[0]);
  const [scrolled, setScrolled] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [mousePos, setMousePos] = useState({ x: -200, y: -200 });
  const [cursorLabel, setCursorLabel] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onMouse = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", onMouse);
    return () => window.removeEventListener("mousemove", onMouse);
  }, []);

  useEffect(() => {
    if (lightbox !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [lightbox]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Inter:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg: #F9F8F6;
          --ink: #0D0D0D;
          --coral: #E8512A;
          --stone: #E4E2DC;
          --cream: #F2EFE9;
          --muted: rgba(13,13,13,0.40);
          --white: #ffffff;
        }

        html { scroll-behavior: smooth; }

        body {
          font-family: 'Inter', sans-serif;
          background: var(--bg);
          color: var(--ink);
          cursor: none;
          -webkit-font-smoothing: antialiased;
        }

        .cursor-dot {
          position: fixed;
          pointer-events: none;
          z-index: 9999;
          width: 10px;
          height: 10px;
          background: var(--coral);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: width 0.2s, height 0.2s, background 0.2s;
          mix-blend-mode: normal;
        }
        .cursor-ring {
          position: fixed;
          pointer-events: none;
          z-index: 9998;
          width: 36px;
          height: 36px;
          border: 1.5px solid rgba(232,81,42,0.45);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: width 0.35s cubic-bezier(.25,.8,.25,1), height 0.35s cubic-bezier(.25,.8,.25,1), border-color 0.25s;
        }
        .cursor-ring.expand {
          width: 68px;
          height: 68px;
          border-color: rgba(232,81,42,0.22);
        }
        .cursor-label {
          position: fixed;
          pointer-events: none;
          z-index: 9999;
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.08em;
          color: var(--coral);
          transform: translate(-50%, 28px);
          white-space: nowrap;
          opacity: 0;
          transition: opacity 0.2s;
        }
        .cursor-label.show { opacity: 1; }

        a, button { cursor: none; }

        /* Nav */
        .nav {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 100;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 32px;
          height: 68px;
          transition: background 0.4s, backdrop-filter 0.4s, border-bottom 0.4s;
        }
        .nav.scrolled {
          background: rgba(249,248,246,0.88);
          backdrop-filter: blur(18px);
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }
        .nav-logo {
          font-family: 'Playfair Display', serif;
          font-size: 17px;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: var(--white);
          transition: color 0.4s;
          text-decoration: none;
        }
        .nav.scrolled .nav-logo { color: var(--ink); }
        .nav-links {
          display: flex;
          gap: 32px;
          list-style: none;
        }
        .nav-links a {
          font-size: 13px;
          font-weight: 400;
          color: rgba(255,255,255,0.65);
          text-decoration: none;
          letter-spacing: 0.01em;
          transition: color 0.25s;
        }
        .nav-links a:hover { color: white; }
        .nav.scrolled .nav-links a { color: var(--muted); }
        .nav.scrolled .nav-links a:hover { color: var(--ink); }
        .nav-cta {
          background: var(--coral);
          color: white !important;
          padding: 10px 22px;
          border-radius: 100px;
          font-size: 13px;
          font-weight: 500;
          text-decoration: none;
          transition: opacity 0.2s, transform 0.2s;
        }
        .nav-cta:hover { opacity: 0.88; transform: translateY(-1px); }

        /* Hero */
        .hero {
          position: relative;
          height: 100vh;
          min-height: 700px;
          background: #111;
          overflow: hidden;
        }
        .hero-bg {
          position: absolute;
          inset: 0;
          background-image: url('/images/hero.jpeg');
          background-size: cover;
          background-position: center;
          transform: scale(1.05);
          animation: heroZoom 12s ease-out forwards;
        }
        @keyframes heroZoom {
          from { transform: scale(1.05); }
          to { transform: scale(1.0); }
        }
        .hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(105deg, rgba(0,0,0,.75) 0%, rgba(0,0,0,.30) 60%, rgba(0,0,0,.10) 100%);
        }
        .hero-content {
          position: absolute;
          bottom: 96px;
          left: 64px;
          max-width: 720px;
          animation: heroFadeUp 1.1s cubic-bezier(.22,1,.36,1) 0.2s both;
        }
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(32px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.55);
          margin-bottom: 20px;
        }
        .hero-eyebrow::before {
          content: '';
          display: block;
          width: 24px;
          height: 1px;
          background: var(--coral);
        }
        .hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(52px, 7vw, 96px);
          font-weight: 600;
          line-height: 0.92;
          letter-spacing: -0.04em;
          color: white;
          margin-bottom: 24px;
        }
        .hero-title em {
          font-style: italic;
          color: rgba(255,255,255,0.7);
        }
        .hero-desc {
          font-size: 15px;
          line-height: 1.8;
          color: rgba(255,255,255,0.55);
          max-width: 480px;
          margin-bottom: 36px;
        }
        .hero-btns {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .btn-primary {
          background: var(--coral);
          color: white;
          padding: 14px 28px;
          border-radius: 100px;
          font-size: 13px;
          font-weight: 500;
          text-decoration: none;
          border: none;
          transition: opacity 0.2s, transform 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .btn-primary:hover { opacity: 0.88; transform: translateY(-2px); }
        .btn-ghost {
          background: rgba(255,255,255,0.10);
          backdrop-filter: blur(10px);
          color: white;
          padding: 14px 28px;
          border-radius: 100px;
          font-size: 13px;
          font-weight: 400;
          text-decoration: none;
          border: 1px solid rgba(255,255,255,0.18);
          transition: background 0.2s;
        }
        .btn-ghost:hover { background: rgba(255,255,255,0.16); }

        /* Hero floating badges */
        .hero-badges {
          position: absolute;
          right: 48px;
          bottom: 96px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          animation: heroFadeUp 1.1s cubic-bezier(.22,1,.36,1) 0.5s both;
        }
        .hero-badge {
          background: rgba(255,255,255,0.10);
          backdrop-filter: blur(16px);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 16px;
          padding: 14px 20px;
          color: white;
          min-width: 160px;
        }
        .hero-badge-val {
          font-family: 'Playfair Display', serif;
          font-size: 26px;
          font-weight: 600;
          letter-spacing: -0.04em;
          line-height: 1;
          margin-bottom: 4px;
        }
        .hero-badge-label {
          font-size: 11px;
          color: rgba(255,255,255,0.50);
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        /* Scroll hint */
        .scroll-hint {
          position: absolute;
          bottom: 36px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: rgba(255,255,255,0.35);
          font-size: 11px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          animation: heroFadeUp 1s 1.2s both;
        }
        .scroll-line {
          width: 1px;
          height: 40px;
          background: linear-gradient(to bottom, rgba(255,255,255,0.5), transparent);
          animation: scrollPulse 2s infinite;
        }
        @keyframes scrollPulse {
          0%, 100% { opacity: 0.4; transform: scaleY(1); }
          50% { opacity: 1; transform: scaleY(0.6); transform-origin: top; }
        }

        .transport-section {
  margin: 0 24px 120px;
  background: #4a4a48;
  border-radius: 32px;
  padding: 48px 64px;
  text-align: center;
}

.transport-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(32px, 4vw, 52px);
  color: white;
  margin-bottom: 12px;
}

.transport-subtitle {
  color: rgba(255,255,255,0.55);
  font-size: 14px;
  margin-bottom: 42px;
}

.transport-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
}

.transport-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: white;
}

.transport-icon {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  border: 2px dashed rgba(255,255,255,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
}

.transport-time {
  font-size: 28px;
  font-weight: 700;
}

.transport-label {
  font-size: 13px;
  color: rgba(255,255,255,0.55);
}

@media (max-width: 900px) {
  .transport-section {
    padding: 42px 24px;
    margin: 0 12px 80px;
  }

  .transport-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;
  }
}

        /* Intro section */
        .intro {
          padding: 120px 64px;
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        .section-tag {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--coral);
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 20px;
        }
        .section-tag::before {
          content: '';
          display: block;
          width: 20px;
          height: 1px;
          background: var(--coral);
        }
        .display-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(36px, 4.5vw, 60px);
          font-weight: 600;
          line-height: 1.05;
          letter-spacing: -0.04em;
          color: var(--ink);
        }
        .display-title em {
          font-style: italic;
          color: var(--muted);
        }
        .body-text {
          font-size: 15px;
          line-height: 1.85;
          color: var(--muted);
          margin-top: 20px;
        }
        .intro-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-top: 40px;
        }
        .stat-card {
          background: var(--cream);
          border-radius: 20px;
          padding: 24px;
          border: 1px solid var(--stone);
        }
        .stat-val {
          font-family: 'Playfair Display', serif;
          font-size: 36px;
          font-weight: 600;
          letter-spacing: -0.04em;
          color: var(--ink);
          line-height: 1;
          margin-bottom: 6px;
        }
        .stat-label {
          font-size: 12px;
          color: var(--muted);
          letter-spacing: 0.04em;
        }
        .intro-img {
          position: relative;
        }
        .intro-img img {
          width: 100%;
          height: 520px;
          object-fit: cover;
          border-radius: 28px;
          display: block;
        }
        .intro-img-tag {
          position: absolute;
          bottom: 24px;
          left: 24px;
          background: white;
          border-radius: 14px;
          padding: 12px 18px;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.12);
        }
        .intro-img-tag-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #22c55e;
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.4); }
          50% { box-shadow: 0 0 0 6px rgba(34,197,94,0); }
        }
        .intro-img-tag-text {
          font-size: 13px;
          font-weight: 500;
          color: var(--ink);
        }

        /* Spaces section */
        .spaces-section {
          padding: 0 64px 120px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .spaces-header {
          text-align: center;
          margin-bottom: 64px;
        }
        .spaces-grid {
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: 24px;
        }
        .spaces-tabs {
          background: var(--cream);
          border-radius: 24px;
          padding: 12px;
          border: 1px solid var(--stone);
          display: flex;
          flex-direction: column;
          gap: 4px;
          align-self: start;
        }
        .space-tab {
          padding: 18px 22px;
          border-radius: 16px;
          background: transparent;
          border: none;
          text-align: left;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 400;
          color: var(--muted);
          transition: background 0.2s, color 0.2s;
          position: relative;
          overflow: hidden;
        }
        .space-tab.active {
          background: white;
          color: var(--ink);
          font-weight: 500;
          box-shadow: 0 8px 30px rgba(0,0,0,0.07);
        }
        .space-tab-tag {
          font-size: 10px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--coral);
          display: block;
          margin-bottom: 2px;
          opacity: 0;
          transition: opacity 0.2s;
        }
        .space-tab.active .space-tab-tag { opacity: 1; }
        .spaces-display {
          display: grid;
          grid-template-columns: 1.2fr 0.8fr;
          gap: 24px;
          align-items: center;
        }
        .space-image-wrap {
          position: relative;
          overflow: hidden;
          border-radius: 24px;
        }
        .space-image-wrap img {
          width: 100%;
          height: 440px;
          object-fit: cover;
          display: block;
          transition: transform 0.6s cubic-bezier(.25,.8,.25,1);
        }
        .space-image-wrap:hover img { transform: scale(1.03); }
        .space-info { padding: 8px 0; }
        .space-info .section-tag { margin-bottom: 16px; }
        .space-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(28px, 3.5vw, 44px);
          font-weight: 600;
          letter-spacing: -0.04em;
          line-height: 1.05;
          color: var(--ink);
          margin-bottom: 20px;
        }

        /* Key figures */
        .figures-section {
          background: var(--ink);
          margin: 0 24px;
          border-radius: 32px;
          padding: 80px 72px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
          position: relative;
          overflow: hidden;
        }
        .figures-section::before {
          content: '';
          position: absolute;
          top: -100px; right: -100px;
          width: 500px; height: 500px;
          background: radial-gradient(circle, rgba(232,81,42,0.15), transparent 70%);
          pointer-events: none;
        }
        .figure-item {
          padding: 0 40px;
          border-right: 1px solid rgba(255,255,255,0.08);
          position: relative;
        }
        .figure-item:first-child { padding-left: 0; }
        .figure-item:last-child { border-right: none; }
        .figure-val {
          font-family: 'Playfair Display', serif;
          font-size: clamp(40px, 5vw, 64px);
          font-weight: 600;
          letter-spacing: -0.05em;
          color: white;
          line-height: 1;
          margin-bottom: 10px;
        }
        .figure-val span { color: var(--coral); }
        .figure-label {
          font-size: 12px;
          color: rgba(255,255,255,0.40);
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        /* Gallery */
        .gallery-section {
          padding: 120px 64px;
          max-width: 1200px;
          margin: 0 auto;
        }
        .gallery-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 48px;
          gap: 40px;
        }
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          grid-template-rows: auto auto;
          gap: 14px;
        }
        .gallery-item {
          position: relative;
          overflow: hidden;
          border-radius: 20px;
          background: var(--stone);
        }
        .gallery-item:nth-child(1) {
          grid-column: span 2;
          grid-row: span 1;
        }
        .gallery-item img {
          width: 100%;
          height: 100%;
          min-height: 240px;
          object-fit: cover;
          display: block;
          transition: transform 0.5s cubic-bezier(.25,.8,.25,1);
        }
        .gallery-item:hover img { transform: scale(1.05); }
        .gallery-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.55), transparent 50%);
          opacity: 0;
          transition: opacity 0.3s;
          display: flex;
          align-items: flex-end;
          padding: 20px;
        }
        .gallery-item:hover .gallery-overlay { opacity: 1; }
        .gallery-overlay-label {
          font-size: 13px;
          font-weight: 500;
          color: white;
          letter-spacing: 0.02em;
        }
        .gallery-view-btn {
          position: absolute;
          top: 16px; right: 16px;
          background: rgba(255,255,255,0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 50%;
          width: 40px; height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s;
          color: white;
          font-size: 18px;
        }
        .gallery-item:hover .gallery-view-btn { opacity: 1; }

        /* Infos */
        .infos-section {
          padding: 0 64px 120px;
          max-width: 900px;
          margin: 0 auto;
          text-align: center;
        }
        .infos-list {
          margin-top: 48px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .info-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: white;
          border-radius: 100px;
          padding: 18px 28px;
          border: 1px solid var(--stone);
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .info-row:hover {
          border-color: var(--coral);
          box-shadow: 0 8px 30px rgba(232,81,42,0.08);
        }
        .info-row-key {
          font-size: 14px;
          font-weight: 500;
          color: var(--ink);
        }
        .info-row-val {
          font-size: 14px;
          color: var(--muted);
        }

        /* Features */
        .features-section {
          padding: 0 24px 120px;
        }
        .features-inner {
          max-width: 1200px;
          margin: 0 auto;
        }
        .features-header { text-align: center; margin-bottom: 64px; }
        .features-grid {
          display: grid;
          grid-template-columns: 1.2fr 1fr 1fr;
          grid-template-rows: auto auto;
          gap: 16px;
        }
        .feature-card {
          background: white;
          border-radius: 24px;
          padding: 32px;
          border: 1px solid var(--stone);
          transition: box-shadow 0.3s, transform 0.3s;
        }
        .feature-card:hover {
          box-shadow: 0 20px 60px rgba(0,0,0,0.08);
          transform: translateY(-4px);
        }
        .feature-card.accent {
          background: var(--coral);
          border-color: transparent;
          grid-row: span 2;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .feature-price {
          font-family: 'Playfair Display', serif;
          font-size: clamp(48px, 6vw, 72px);
          font-weight: 600;
          letter-spacing: -0.05em;
          color: white;
          line-height: 1;
          margin-bottom: 16px;
        }
        .feature-price-sub {
          font-size: 14px;
          color: rgba(255,255,255,0.65);
          line-height: 1.7;
        }
        .feature-card-icon {
          width: 44px; height: 44px;
          background: var(--cream);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          margin-bottom: 20px;
          border: 1px solid var(--stone);
        }
        .feature-card.accent .feature-card-icon {
          background: rgba(255,255,255,0.15);
          border-color: rgba(255,255,255,0.2);
        }
        .feature-card-title {
          font-family: 'Playfair Display', serif;
          font-size: 20px;
          font-weight: 600;
          letter-spacing: -0.03em;
          color: var(--ink);
          margin-bottom: 10px;
        }
        .feature-card.accent .feature-card-title { color: white; }
        .feature-card-text {
          font-size: 13px;
          line-height: 1.75;
          color: var(--muted);
        }
        .feature-card.accent .feature-card-text { color: rgba(255,255,255,0.65); }
        .feature-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 100px;
          padding: 8px 14px;
          font-size: 12px;
          color: white;
          margin-top: auto;
        }
        .feature-badge::before {
          content: '';
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #86efac;
        }

        /* Footer contact */
        .footer {
          margin: 0 24px 24px;
          background: var(--ink);
          border-radius: 32px;
          padding: 80px 72px;
          position: relative;
          overflow: hidden;
        }
        .footer::after {
          content: '';
          position: absolute;
          bottom: -120px; left: -80px;
          width: 400px; height: 400px;
          background: radial-gradient(circle, rgba(232,81,42,0.12), transparent 70%);
          pointer-events: none;
        }
        .footer-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
          position: relative;
          z-index: 1;
        }
        .footer-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(36px, 4vw, 52px);
          font-weight: 600;
          letter-spacing: -0.04em;
          color: white;
          line-height: 1.05;
          margin-bottom: 20px;
        }
        .footer-desc {
          font-size: 14px;
          line-height: 1.8;
          color: rgba(255, 255, 255, 0.56);
        }
        .contact-links {
          display: flex;
          flex-direction: column;
          gap: 16px;
          justify-content: center;
        }
        .contact-link {
          display: flex;
          align-items: center;
          gap: 20px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 20px;
          padding: 24px 28px;
          text-decoration: none;
          transition: background 0.2s, border-color 0.2s, transform 0.2s;
        }
        .contact-link:hover {
          background: rgba(255,255,255,0.10);
          border-color: rgba(232,81,42,0.4);
          transform: translateX(4px);
        }
        .contact-link-icon {
          width: 48px; height: 48px;
          border-radius: 14px;
          background: var(--coral);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }
        .contact-link-body {}
        .contact-link-label {
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.35);
          margin-bottom: 4px;
        }
        .contact-link-value {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          font-weight: 600;
          letter-spacing: -0.03em;
          color: white;
        }
        .contact-link-arrow {
          margin-left: auto;
          color: rgba(255,255,255,0.25);
          font-size: 18px;
          transition: color 0.2s, transform 0.2s;
        }
        .contact-link:hover .contact-link-arrow {
          color: var(--coral);
          transform: translateX(4px);
        }

        .footer-bottom {
          margin-top: 56px;
          padding-top: 28px;
          border-top: 1px solid rgba(255,255,255,0.08);
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.73);
          letter-spacing: 0.02em;
          position: relative;
          z-index: 1;
        }

        /* Lightbox */
        .lightbox {
          position: fixed;
          inset: 0;
          z-index: 999;
          background: rgba(0,0,0,0.92);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px;
          backdrop-filter: blur(4px);
          animation: fadeIn 0.25s ease;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .lightbox img {
          max-width: 100%;
          max-height: 90vh;
          border-radius: 20px;
          object-fit: contain;
          box-shadow: 0 40px 120px rgba(0,0,0,0.6);
        }
        .lightbox-close {
          position: absolute;
          top: 28px; right: 28px;
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 50%;
          width: 48px; height: 48px;
          color: white;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        .lightbox-close:hover { background: rgba(255,255,255,0.18); }
        .lightbox-label {
          position: absolute;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 13px;
          color: rgba(255,255,255,0.5);
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        @media (max-width: 900px) {
          .nav-links { display: none; }
          .hero-content { left: 24px; bottom: 80px; }
          .hero-badges { display: none; }
          .intro { grid-template-columns: 1fr; padding: 80px 24px; gap: 48px; }
          .spaces-section { padding: 0 24px 80px; }
          .spaces-grid { grid-template-columns: 1fr; }
          .spaces-tabs { flex-direction: row; overflow-x: auto; }
          .spaces-display { grid-template-columns: 1fr; }
          .figures-section { grid-template-columns: 1fr 1fr; padding: 48px 32px; margin: 0 12px; }
          .figure-item { padding: 20px; border-right: none; border-bottom: 1px solid rgba(255,255,255,0.08); }
          .figure-item:nth-child(odd) { border-right: 1px solid rgba(255,255,255,0.08); }
          .gallery-section { padding: 80px 24px; }
          .gallery-grid { grid-template-columns: 1fr 1fr; }
          .gallery-item:nth-child(1) { grid-column: span 2; }
          .infos-section { padding: 0 24px 80px; }
          .features-section { padding: 0 12px 80px; }
          .features-grid { grid-template-columns: 1fr; }
          .feature-card.accent { grid-row: span 1; }
          .footer { padding: 56px 32px; margin: 0 12px 12px; }
          .footer-grid { grid-template-columns: 1fr; gap: 48px; }
          .form-row { grid-template-columns: 1fr; }
          .footer-bottom { flex-direction: column; gap: 8px; text-align: center; }
        }
      `}</style>

      {/* Custom cursor */}
      <div
        className="cursor-dot"
        style={{ left: mousePos.x, top: mousePos.y }}
      />
      <div
        className={`cursor-ring ${cursorLabel ? "expand" : ""}`}
        style={{ left: mousePos.x, top: mousePos.y }}
      />
      <div
        className={`cursor-label ${cursorLabel ? "show" : ""}`}
        style={{ left: mousePos.x, top: mousePos.y }}
      >
        {cursorLabel}
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <img
            src={galleryImages[lightbox].src}
            alt={galleryImages[lightbox].label}
            onClick={(e) => e.stopPropagation()}
          />
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          <div className="lightbox-label">{galleryImages[lightbox].label}</div>
        </div>
      )}

      {/* Nav */}
      <nav className={`nav ${scrolled ? "scrolled" : ""}`}>
        <a href="#" className="nav-logo">T3 Chambéry</a>
        <ul className="nav-links">
          <li><a href="#bien">Le bien</a></li>
          <li><a href="#photos">Photos</a></li>
          <li><a href="#infos">Infos</a></li>
          <li><a href="#contact">Contact</a></li>
        </ul>
        <a
          href="#contact"
          className="nav-cta"
          onMouseEnter={() => setCursorLabel("Visiter")}
          onMouseLeave={() => setCursorLabel("")}
        >
          Demander une visite
        </a>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="hero-eyebrow">Chambéry — Canal des Usiniers</div>
          <h1 className="hero-title">
            T3 de 58 m²<br /><em>avec terrasse</em>
          </h1>
          <p className="hero-desc">
            Un appartement de plain-pied, lumineux et bien agencé, avec deux chambres, une terrasse privative de 14 m² et deux places de parking.
          </p>
          <div className="hero-btns">
            <a href="#photos" className="btn-primary"
              onMouseEnter={() => setCursorLabel("Voir")}
              onMouseLeave={() => setCursorLabel("")}
            >
              Voir les photos
            </a>
            <a href="#contact" className="btn-ghost"
              onMouseEnter={() => setCursorLabel("Visiter")}
              onMouseLeave={() => setCursorLabel("")}
            >
              Demander une visite
            </a>
          </div>
        </div>

        <div className="hero-badges">
          {[
            { val: "210K€", label: "Net vendeur" },
            { val: "58 m²", label: "Surface habitable" },
            { val: "14 m²", label: "Terrasse privative" },
          ].map((b) => (
            <div className="hero-badge" key={b.label}>
              <div className="hero-badge-val">{b.val}</div>
              <div className="hero-badge-label">{b.label}</div>
            </div>
          ))}
        </div>

        <div className="scroll-hint">
          <span>Découvrir</span>
          <div className="scroll-line" />
        </div>
      </section>

      {/* Intro */}
      <section id="bien">
        <div className="intro">
          <Reveal>
            <div className="section-tag">Appartement T3 à vendre</div>
            <h2 className="display-title">
              Un bien rare entre <em>confort,</em> extérieur et emplacement
            </h2>
            <p className="body-text">
              Situé vers le Canal des Usiniers à Chambéry, cet appartement réunit les critères essentiels d'un bien attractif : plain-pied, volumes généreux, terrasse et deux stationnements inclus.
            </p>
            <div className="intro-stats">
              {[
                { val: "58 m²", label: "Surface loi Carrez" },
                { val: "14 m²", label: "Terrasse privative" },
                { val: "2", label: "Chambres" },
                { val: "2", label: "Places de parking" },
              ].map((s) => (
                <div className="stat-card" key={s.label}>
                  <div className="stat-val">{s.val}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="intro-img">
              <img src="/images/terrasse.jpeg" alt="Terrasse" />
              <div className="intro-img-tag">
                <div className="intro-img-tag-dot" />
                <div className="intro-img-tag-text">Bien disponible à la vente</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Spaces */}
      <section>
        <div className="spaces-section">
          <Reveal>
            <div className="spaces-header">
              <div className="section-tag" style={{ justifyContent: "center" }}>Les espaces</div>
              <h2 className="display-title">Chaque pièce a son caractère</h2>
            </div>
          </Reveal>
          <div className="spaces-grid">
            <Reveal delay={0.05}>
              <div className="spaces-tabs">
                {spaces.map((item) => (
                  <button
                    key={item.label}
                    className={`space-tab ${selected.label === item.label ? "active" : ""}`}
                    onClick={() => setSelected(item)}
                    onMouseEnter={() => setCursorLabel(item.label)}
                    onMouseLeave={() => setCursorLabel("")}
                  >
                    <span className="space-tab-tag">{item.tag}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="spaces-display">
                <div className="space-image-wrap">
                  <img
                    key={selected.image}
                    src={selected.image}
                    alt={selected.label}
                    style={{ animation: "heroFadeUp 0.5s ease both" }}
                  />
                </div>
                <div className="space-info">
                  <div className="section-tag">{selected.tag}</div>
                  <h3 className="space-title">{selected.title}</h3>
                  <p className="body-text">{selected.text}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Key figures */}
      <Reveal>
        <div className="figures-section">
          {[
            { val: <><span>210</span> 000 €</>, label: "Net vendeur" },
            { val: "58 m²", label: "Surface habitable" },
            { val: "14 m²", label: "Terrasse" },
            { val: "2 P.", label: "Places de parking" },
          ].map((f, i) => (
            <div className="figure-item" key={i}>
              <div className="figure-val">{f.val}</div>
              <div className="figure-label">{f.label}</div>
            </div>
          ))}
        </div>
      </Reveal>

      {/* Gallery */}
      <section id="photos">
        <div className="gallery-section">
          <Reveal>
            <div className="gallery-header">
              <div>
                <div className="section-tag">Galerie photos</div>
                <h2 className="display-title">Découvrez chaque espace</h2>
              </div>
              <a href="#contact" className="btn-primary"
                style={{ flexShrink: 0 }}
                onMouseEnter={() => setCursorLabel("Visiter")}
                onMouseLeave={() => setCursorLabel("")}
              >
                Planifier une visite
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="gallery-grid">
              {galleryImages.map((img, i) => (
                <div
                  className="gallery-item"
                  key={img.src}
                  onMouseEnter={() => setCursorLabel("Agrandir")}
                  onMouseLeave={() => setCursorLabel("")}
                  onClick={() => setLightbox(i)}
                >
                  <img src={img.src} alt={img.label} />
                  <div className="gallery-overlay">
                    <span className="gallery-overlay-label">{img.label}</span>
                  </div>
                  <button className="gallery-view-btn">+</button>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Transport */}
      <section>
        <Reveal>
          <div className="transport-section">
            <h2 className="transport-title">À deux pas du centre-ville</h2>
            <p className="transport-subtitle">
              Depuis le Canal des Usiniers, rejoignez rapidement le centre de Chambéry selon votre mode de transport.
            </p>

            <div className="transport-grid">
              {transportTimes.map((item) => (
                <div className="transport-item" key={item.label}>
                  <div className="transport-icon">
                    <item.icon size={34} strokeWidth={1.7} />
                  </div>
                  <div className="transport-time">{item.value}</div>
                  <div className="transport-label">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* Info table */}
      <section id="infos">
        <div className="infos-section">
          <Reveal>
            <div className="section-tag" style={{ justifyContent: "center" }}>Informations clés</div>
            <h2 className="display-title">Ce qu'il faut savoir</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="infos-list">
              {[
                ["Adresse", "Canal des Usiniers, Chambéry"],
                ["Prix net vendeur", "210 000 €"],
                ["Surface habitable", "58 m²"],
                ["Terrasse privative", "14 m²"],
                ["Chambres", "2 chambres"],
                ["Niveau", "Plain-pied"],
                ["Stationnement", "2 places de parking"],
                ["Cave", "Non"],
              ].map(([key, val]) => (
                <div className="info-row" key={key}>
                  <span className="info-row-key">{key}</span>
                  <span className="info-row-val">{val}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Feature cards */}
      <section>
        <div className="features-section">
          <div className="features-inner">
            <Reveal>
              <div className="features-header">
                <div className="section-tag" style={{ justifyContent: "center" }}>Les atouts</div>
                <h2 className="display-title">Un bien qui coche <em>toutes les cases</em></h2>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="features-grid">
                <div className="feature-card accent">
                  <div>
                    <div className="feature-card-icon">
                      <BadgeEuro size={22} strokeWidth={1.8} />
                    </div>
                    <div className="feature-price">210<br />000 €</div>
                    <div className="feature-price-sub">Prix net vendeur pour un T3 de 58 m² avec terrasse et deux places de parking à Chambéry.</div>
                  </div>
                  <div className="feature-badge">Disponible maintenant</div>
                </div>

                <div className="feature-card">
                  <div className="feature-card-icon">
                    <MapPin size={22} strokeWidth={1.8} />
                  </div>
                  <div className="feature-card-title">Canal des Usiniers</div>
                  <div className="feature-card-text">Secteur prisé, accès rapide au centre-ville, transports et commerces à proximité.</div>
                </div>

                <div className="feature-card">
                  <div className="feature-card-icon">
                    <Leaf size={22} strokeWidth={1.8} />
                  </div>
                  <div className="feature-card-title">Terrasse de 14 m²</div>
                  <div className="feature-card-text">Un vrai espace extérieur privé, rare en appartement à Chambéry.</div>
                </div>

                <div className="feature-card">
                  <div className="feature-card-icon">
                    <DoorOpen size={22} strokeWidth={1.8} />
                  </div>
                  <div className="feature-card-title">Deux chambres</div>
                  <div className="feature-card-text">Bien adapté à une famille, un couple avec bureau ou un investissement locatif.</div>
                </div>

                <div className="feature-card">
                  <div className="feature-card-icon">
                    <Car size={22} strokeWidth={1.8} />
                  </div>
                  <div className="feature-card-title">Deux parkings</div>
                  <div className="feature-card-text">Un confort rare et très recherché au quotidien en ville.</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Footer / Contact */}
      <footer id="contact">
        <div className="footer">
          <div className="footer-grid">
            <Reveal>
              <div>
                <div className="section-tag" style={{ color: "rgba(255,255,255,0.35)" }}>
                  <span style={{ background: "rgba(255,255,255,0.35)" }} />
                  Contact
                </div>
                <h2 className="footer-title">Vous souhaitez visiter ce T3 ?</h2>
                <p className="footer-desc">
                  Demandez les informations complètes, des photos supplémentaires ou planifiez une visite du bien situé vers le Canal des Usiniers.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="contact-links">
                <a
                  href="tel:+33633160223"
                  className="contact-link"
                  onMouseEnter={() => setCursorLabel("Appeler")}
                  onMouseLeave={() => setCursorLabel("")}
                >
                  <div className="contact-link-icon">
                    <Phone size={22} strokeWidth={1.8} />
                  </div>
                  <div className="contact-link-body">
                    <div className="contact-link-label">Téléphone</div>
                    <div className="contact-link-value">06 33 16 02 23</div>
                  </div>
                  <div className="contact-link-arrow">→</div>
                </a>
                <a
                  href="mailto:carole.vialelle@gmail.com"
                  className="contact-link"
                  onMouseEnter={() => setCursorLabel("Écrire")}
                  onMouseLeave={() => setCursorLabel("")}
                >
                  <div className="contact-link-icon">
                    <Mail size={22} strokeWidth={1.8} />
                  </div>
                  <div className="contact-link-body">
                    <div className="contact-link-label">Email</div>
                    <div className="contact-link-value">carole.vialelle@gmail.com</div>
                  </div>
                  <div className="contact-link-arrow">→</div>
                </a>
              </div>
            </Reveal>
          </div>
          <div className="footer-bottom">
            <span>T3 — Canal des Usiniers, Chambéry</span>
            <span>58 m² · 2 chambres · Terrasse 14 m² · 2 parkings · 210 000 € net vendeur</span>
             <a
                          href="https://noxzi.fr"
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-3"
                        >
                          <span>Propulsé par</span>
                          <img
                            src={logoNoxzi}
                            alt="Logo NoxZi"
                            className="h-7 w-auto object-contain"
                            loading="lazy"
                          />
                        </a>
          </div>
        </div>
      </footer>
    </>
  );
}